import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { testimonialApi } from 'api/testimonialApi';

export const saveTestimonial = createAsyncThunk(
  'testimonial/saveTestimonial',
  async (testimonialData, { rejectWithValue }) => {
    try {
      const response = await testimonialApi.saveTestimonial(testimonialData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save testimonial');
    }
  }
);

export const assignTestimonialToPages = createAsyncThunk(
  'testimonial/assignTestimonialToPages',
  async ({ testimonialId, pageAssignments }, { rejectWithValue }) => {
    try {
      const response = await testimonialApi.assignTestimonialToPages(testimonialId, pageAssignments);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to assign testimonial to pages');
    }
  }
);

export const fetchAllTestimonials = createAsyncThunk(
  'testimonial/fetchAllTestimonials',
  async (_, { rejectWithValue }) => {
    try {
      const response = await testimonialApi.getAllTestimonials();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch testimonials');
    }
  }
);

export const fetchTestimonialAssignments = createAsyncThunk(
  'testimonial/fetchTestimonialAssignments',
  async (testimonialId, { rejectWithValue }) => {
    try {
      const response = await testimonialApi.getTestimonialAssignments(testimonialId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch testimonial assignments');
    }
  }
);

export const deleteTestimonial = createAsyncThunk(
  'testimonial/deleteTestimonial',
  async (testimonialId, { rejectWithValue }) => {
    try {
      await testimonialApi.deleteTestimonial(testimonialId);
      return testimonialId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete testimonial');
    }
  }
);

export const updateTestimonialStatus = createAsyncThunk(
  'testimonial/updateTestimonialStatus',
  async ({ testimonialId, active }, { rejectWithValue }) => {
    try {
      const response = await testimonialApi.updateTestimonialStatus(testimonialId, active);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update testimonial status');
    }
  }
);

const initialState = {
  allTestimonialsList: [],
  testimonialAssignments: [],
  loading: false,
  submitting: false,
  assigning: false,
  error: null
};

const testimonialSlice = createSlice({
  name: 'testimonial',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTestimonialAssignments: (state) => {
      state.testimonialAssignments = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Save Testimonial
      .addCase(saveTestimonial.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(saveTestimonial.fulfilled, (state, action) => {
        state.submitting = false;
        const existingIndex = state.allTestimonialsList.findIndex(t => t.id === action.payload.id);
        if (existingIndex !== -1) {
          state.allTestimonialsList[existingIndex] = action.payload;
        } else {
          state.allTestimonialsList.unshift(action.payload);
        }
      })
      .addCase(saveTestimonial.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Assign Testimonial to Pages
      .addCase(assignTestimonialToPages.pending, (state) => {
        state.assigning = true;
        state.error = null;
      })
      .addCase(assignTestimonialToPages.fulfilled, (state, action) => {
        state.assigning = false;
        const existingIndex = state.allTestimonialsList.findIndex(t => t.id === action.payload.id);
        if (existingIndex !== -1) {
          state.allTestimonialsList[existingIndex] = action.payload;
        }
      })
      .addCase(assignTestimonialToPages.rejected, (state, action) => {
        state.assigning = false;
        state.error = action.payload;
      })

      // Fetch All Testimonials
      .addCase(fetchAllTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTestimonials.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.testimonials && Array.isArray(action.payload.testimonials)) {
          state.allTestimonialsList = action.payload.testimonials;
        } else if (Array.isArray(action.payload)) {
          state.allTestimonialsList = action.payload;
        } else {
          state.allTestimonialsList = [];
        }
      })
      .addCase(fetchAllTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.allTestimonialsList = [];
      })

      // Fetch Testimonial Assignments
      .addCase(fetchTestimonialAssignments.pending, (state) => {
        state.assigning = true;
        state.error = null;
      })
      .addCase(fetchTestimonialAssignments.fulfilled, (state, action) => {
        state.assigning = false;
        state.testimonialAssignments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchTestimonialAssignments.rejected, (state, action) => {
        state.assigning = false;
        state.error = action.payload;
        state.testimonialAssignments = [];
      })

      // Delete Testimonial
      .addCase(deleteTestimonial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTestimonial.fulfilled, (state, action) => {
        state.loading = false;
        state.allTestimonialsList = state.allTestimonialsList.filter(testimonial => testimonial.id !== action.payload);
      })
      .addCase(deleteTestimonial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Testimonial Status
      .addCase(updateTestimonialStatus.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateTestimonialStatus.fulfilled, (state, action) => {
        state.submitting = false;
        const existingIndex = state.allTestimonialsList.findIndex(t => t.id === action.payload.id);
        if (existingIndex !== -1) {
          state.allTestimonialsList[existingIndex] = action.payload;
        }
      })
      .addCase(updateTestimonialStatus.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  clearTestimonialAssignments 
} = testimonialSlice.actions;

export const selectAllTestimonialsList = (state) => state.testimonial.allTestimonialsList;
export const selectTestimonialAssignments = (state) => state.testimonial.testimonialAssignments;
export const selectTestimonialLoading = (state) => state.testimonial.loading;
export const selectTestimonialSubmitting = (state) => state.testimonial.submitting;
export const selectTestimonialAssigning = (state) => state.testimonial.assigning;
export const selectTestimonialError = (state) => state.testimonial.error;

export default testimonialSlice.reducer;
